/**
 * Connects to the server's SSE stream and reacts to real-time file events.
 *
 * On a file.uploaded event the hook invalidates:
 *   - ['files', folderId]        → refreshes the file list in the open folder
 *   - ['notifications']          → refreshes the notification bell count
 *
 * EventSource doesn't support Authorization headers, so we use fetch() with
 * a ReadableStream reader and reconstruct the SSE framing manually.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { tokenStore } from '@/lib/auth';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const RECONNECT_DELAY_MS = 5_000;

export function useSSESync(enabled: boolean) {
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    async function connect() {
      const token = tokenStore.getAccess();
      if (!token || cancelled) return;

      const ctrl = new AbortController();
      abortRef.current = ctrl;

      try {
        const res = await fetch(`${BASE_URL}/notifications/events`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          signal: ctrl.signal,
        });

        if (res.status === 401) {
          // Token expired — give the axios interceptor a chance to refresh,
          // then reconnect.
          if (!cancelled) reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
          return;
        }

        if (!res.ok || !res.body) {
          if (!cancelled) reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done || cancelled) break;

          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            try {
              const event = JSON.parse(json) as {
                type?: string;
                folder_id?: string;
                file_id?: string;
              };
              if (event.type === 'file.uploaded') {
                if (event.folder_id) {
                  qc.invalidateQueries({ queryKey: ['files', event.folder_id] });
                }
                qc.invalidateQueries({ queryKey: ['notifications'] });
              }
            } catch {
              // ignore malformed event
            }
          }
        }
      } catch {
        // fetch aborted or network error
      }

      if (!cancelled) {
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [enabled, qc]);
}
